import { useMemo } from 'react';
import { Course, CourseCapsule } from '../../../types/course';
import { useCourses } from './useCourses';

interface UseCourseCapsuleResult {
  course: Course | null;
  capsule: CourseCapsule | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCourseCapsule(
  courseSlug: string | null,
  capsuleSlug: string | null
): UseCourseCapsuleResult {
  const { courses, loading, error, refetch } = useCourses();

  const course = useMemo(() => {
    if (!courseSlug) return null;
    return courses.find((c) => c.slug === courseSlug) || null;
  }, [courses, courseSlug]);

  const capsule = useMemo(() => {
    if (!course || !capsuleSlug) return null;
    return course.capsules.find((c) => c.slug === capsuleSlug) || null;
  }, [course, capsuleSlug]);

  const derivedError = useMemo(() => {
    if (error) return error;
    if (!courseSlug) return 'No course specified';
    if (!capsuleSlug) return 'No capsule specified';
    if (!loading && !course) return `Course "${courseSlug}" not found`;
    if (!loading && course && !capsule) return `Capsule "${capsuleSlug}" not found in course`;
    return null;
  }, [capsule, capsuleSlug, course, courseSlug, error, loading]);

  return {
    course,
    capsule,
    loading,
    error: derivedError,
    refetch,
  };
}
